import { parseModule } from './modules/toc.js';
import { captureSelection, findEnclosingSectionTitle } from './modules/capture.js';
import { ApiClient, AuthError, PayloadTooLargeError, RateLimitError, TransformError } from './modules/api-client.js';
import { SpendTracker } from './modules/spend-tracker.js';
import { setupPinGate } from './modules/pin-gate.js';

const api = new ApiClient();
const spend = new SpendTracker();

const els = {
  pinGate: document.getElementById('pin-gate'),
  app: document.getElementById('app'),
  emptyState: document.getElementById('empty-state'),
  fileInput: document.getElementById('file-input'),
  workspace: document.getElementById('workspace'),
  moduleLabel: document.getElementById('module-label'),
  tocList: document.getElementById('toc-list'),
  resetModule: document.getElementById('reset-module'),
  previewIframe: document.getElementById('preview-iframe'),
  captureButton: document.getElementById('capture-button'),
  capturedStatus: document.getElementById('captured-status'),
  capturedMeta: document.getElementById('captured-meta'),
  newCapture: document.getElementById('new-capture'),
  outputIframe: document.getElementById('output-iframe'),
  outputSource: document.getElementById('output-source'),
  messageList: document.getElementById('message-list'),
  instructionInput: document.getElementById('instruction-input'),
  sendButton: document.getElementById('send-button'),
  lockButton: document.getElementById('lock-button'),
  spendPill: document.getElementById('spend-pill')
};

const state = {
  module: { title: '', sections: [], doc: null },
  captured: null,
  // captured shape when set:
  // {
  //   section_html: string,
  //   section_title: string,
  //   messages: Array<{ role: 'user' | 'assistant', content: string }>,
  //   locked: boolean
  // }
  activeTab: 'preview',
  sending: false
};

// =============================================================
// Spend pill + button enabled state
// =============================================================

function updateSpendPill() {
  els.spendPill.textContent = spend.format();
  els.spendPill.classList.toggle('warning', spend.getStatus() === 'warning');
  els.spendPill.classList.toggle('blocked', spend.getStatus() === 'blocked');
  refreshButtons();
}

function refreshButtons() {
  const hasCapture = !!state.captured;
  const hasInstruction = els.instructionInput.value.trim().length > 0;
  const locked = state.captured?.locked === true;
  const hasOutput = state.captured?.messages.some((m) => m.role === 'assistant') === true;

  els.sendButton.disabled = !hasCapture || !hasInstruction || locked || state.sending || !spend.canSpend();
  els.lockButton.disabled = !hasOutput || locked || state.sending;
  els.newCapture.disabled = !hasCapture && !state.module.title;
  els.instructionInput.disabled = !hasCapture || locked || state.sending;
  els.captureButton.disabled = !state.module.title || state.sending;
}

// =============================================================
// Module loading + TOC nav
// =============================================================

function handleFile(file) {
  const reader = new FileReader();
  reader.onload = () => {
    try {
      const doc = new DOMParser().parseFromString(String(reader.result), 'text/html');
      renderModule(doc);
    } catch (err) {
      alert(`Could not parse module: ${err.message}`);
    }
  };
  reader.readAsText(file);
}

function renderModule(parsedDoc) {
  const { moduleTitle, sections } = parseModule(parsedDoc);
  state.module = { title: moduleTitle, sections, doc: parsedDoc };
  state.captured = null;

  els.moduleLabel.textContent = moduleTitle;
  els.tocList.innerHTML = '';
  sections.forEach((section) => {
    const link = document.createElement('button');
    link.type = 'button';
    link.className = 'toc-link';
    link.textContent = section.title;
    link.dataset.sectionId = section.id;
    link.addEventListener('click', () => scrollToSection(section.id, link));
    els.tocList.appendChild(link);
  });

  const html = '<!doctype html>' + parsedDoc.documentElement.outerHTML;
  els.previewIframe.srcdoc = html;

  els.emptyState.classList.add('hidden');
  els.workspace.classList.remove('hidden');

  updateCapturedDisplay();
  resetOutput();
  refreshButtons();
}

function scrollToSection(sectionId, linkEl) {
  const iframeDoc = els.previewIframe.contentDocument;
  if (!iframeDoc) return;
  const target = iframeDoc.getElementById(sectionId);
  if (target?.scrollIntoView) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  // Active highlight
  els.tocList.querySelectorAll('.toc-link').forEach((el) => el.classList.remove('active'));
  if (linkEl) linkEl.classList.add('active');
}

function resetToEmpty() {
  if (state.captured && state.captured.messages.length > 0 && !state.captured.locked) {
    if (!confirm('You have an unsaved conversation. Discard and load a new module?')) return;
  }
  state.module = { title: '', sections: [], doc: null };
  state.captured = null;
  els.previewIframe.srcdoc = '';
  els.tocList.innerHTML = '';
  els.moduleLabel.textContent = '';
  els.workspace.classList.add('hidden');
  els.emptyState.classList.remove('hidden');
  updateCapturedDisplay();
  resetOutput();
  refreshButtons();
}

// =============================================================
// Drag-to-select capture
// =============================================================

function onCaptureClick() {
  if (!state.module.title) return;
  // If there's an in-progress conversation, warn before discarding.
  if (state.captured && state.captured.messages.length > 0 && !state.captured.locked) {
    if (!confirm('Capturing new selection will discard the current conversation. Continue?')) return;
  }
  const iframeWindow = els.previewIframe.contentWindow;
  if (!iframeWindow) {
    alert('Preview not ready. Reload the module and try again.');
    return;
  }
  let html;
  try {
    html = captureSelection(iframeWindow);
  } catch (err) {
    alert(err.message);
    return;
  }
  // Derive section title from the selection's start container.
  const selection = iframeWindow.getSelection();
  const startNode = selection.getRangeAt(0).startContainer;
  const startEl = startNode.nodeType === 1 ? startNode : startNode.parentElement;
  const sectionTitle = findEnclosingSectionTitle(startEl);

  state.captured = {
    section_html: html,
    section_title: sectionTitle,
    messages: [],
    locked: false
  };

  updateCapturedDisplay();
  resetOutput();
  els.instructionInput.value = '';
  els.instructionInput.focus();
  refreshButtons();
}

function onNewCapture() {
  if (state.captured && state.captured.messages.length > 0 && !state.captured.locked) {
    if (!confirm('Discard the current conversation?')) return;
  }
  state.captured = null;
  els.instructionInput.value = '';
  updateCapturedDisplay();
  resetOutput();
  refreshButtons();
}

function updateCapturedDisplay() {
  if (!state.captured) {
    els.capturedMeta.textContent = 'No section captured yet.';
    els.capturedMeta.classList.remove('has-capture');
    els.capturedStatus.textContent = 'No section captured';
    return;
  }
  const turnCount = state.captured.messages.filter((m) => m.role === 'user').length;
  const label = `"${state.captured.section_title}" — ${state.captured.section_html.length.toLocaleString()} chars` +
    (turnCount > 0 ? ` — ${turnCount} turn${turnCount === 1 ? '' : 's'}` : '') +
    (state.captured.locked ? ' — locked' : '');
  els.capturedMeta.textContent = label;
  els.capturedMeta.classList.add('has-capture');
  els.capturedStatus.textContent = `Captured: ${state.captured.section_title}`;
}

// =============================================================
// Send + multi-turn chat
// =============================================================

async function onSend() {
  if (!state.captured || state.captured.locked || state.sending) return;
  const instruction = els.instructionInput.value.trim();
  if (!instruction) return;
  if (!spend.canSpend()) {
    alert('Daily API spend cap reached. Wait for tomorrow or contact Richard.');
    return;
  }

  // Append the user message
  state.captured.messages.push({ role: 'user', content: instruction });

  state.sending = true;
  els.sendButton.textContent = 'Thinking...';
  refreshButtons();
  renderHistory();

  try {
    const result = await api.transform({
      module_title: state.module.title,
      section_title: state.captured.section_title,
      section_html: state.captured.section_html,
      messages: state.captured.messages
    });

    // Append assistant message
    state.captured.messages.push({ role: 'assistant', content: result.html });

    spend.add({
      tokens: result.usage.input_tokens + result.usage.output_tokens,
      costUSD: result.usage.cost_usd
    });

    els.instructionInput.value = '';
    renderOutput(result.html);
    renderHistory();
    updateCapturedDisplay();
    updateSpendPill();
  } catch (err) {
    // Roll back: remove the user message we just appended
    state.captured.messages.pop();
    handleApiError(err);
  } finally {
    state.sending = false;
    els.sendButton.textContent = 'Send';
    refreshButtons();
    renderHistory();
  }
}

function handleApiError(err) {
  if (err instanceof AuthError) {
    alert(err.message + '\n\nReloading to re-enter PIN.');
    location.reload();
  } else if (err instanceof PayloadTooLargeError) {
    alert(`Too large: ${err.message}. Try a smaller selection or shorter follow-up.`);
  } else if (err instanceof RateLimitError) {
    alert('Rate limit reached. Wait a minute and try again.');
  } else if (err instanceof TransformError) {
    if (confirm(err.message + '\n\nCopy the prompt to clipboard so you can paste it into your claude.ai?')) {
      showFallback();
    }
  } else {
    alert('Unexpected error: ' + err.message);
  }
}

function showFallback() {
  if (!state.captured) return;
  const lastUser = state.captured.messages.filter((m) => m.role === 'user').pop();
  const fallback = `Module: ${state.module.title}
Section: ${state.captured.section_title}

Captured HTML:
${state.captured.section_html}

Instruction: ${lastUser?.content || ''}`;
  navigator.clipboard.writeText(fallback).catch(() => {});
  alert('Prompt copied to clipboard. Paste into claude.ai.');
}

// =============================================================
// Output rendering (Preview / HTML source / History tabs)
// =============================================================

function resetOutput() {
  els.outputIframe.srcdoc = '<!doctype html><html><body></body></html>';
  els.outputSource.textContent = 'Output will appear here once you send an instruction.';
  renderHistory();
}

function renderOutput(html) {
  els.outputIframe.srcdoc = `<!doctype html><html><body>${html}</body></html>`;
  els.outputSource.textContent = html;
}

function renderHistory() {
  els.messageList.innerHTML = '';
  if (!state.captured || state.captured.messages.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'history-empty';
    empty.textContent = 'Conversation history will appear here.';
    els.messageList.appendChild(empty);
    return;
  }
  state.captured.messages.forEach((msg) => {
    const div = document.createElement('div');
    div.className = `message ${msg.role}`;
    const role = document.createElement('div');
    role.className = 'message-role';
    role.textContent = msg.role === 'user' ? 'You' : 'Claude';
    div.appendChild(role);
    const body = document.createElement('div');
    body.textContent = msg.content;
    div.appendChild(body);
    els.messageList.appendChild(div);
  });
}

function switchTab(tab) {
  state.activeTab = tab;
  document.querySelectorAll('.tab').forEach((el) => {
    el.classList.toggle('active', el.dataset.tab === tab);
  });
  document.querySelectorAll('.tab-panel').forEach((el) => {
    el.classList.toggle('active', el.dataset.panel === tab);
  });
}

// =============================================================
// Lock + download
// =============================================================

function onLock() {
  if (!state.captured) return;
  const lastAssistant = [...state.captured.messages].reverse().find((m) => m.role === 'assistant');
  if (!lastAssistant) return;
  downloadHTML(lastAssistant.content, state.captured.section_title);
  state.captured.locked = true;
  updateCapturedDisplay();
  refreshButtons();
}

function downloadHTML(html, sectionTitle) {
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safe = (sectionTitle || 'section').replace(/[^a-z0-9-]+/gi, '-').toLowerCase();
  const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
  a.download = `harvest-${safe}-${stamp}.html`;
  a.click();
  URL.revokeObjectURL(url);
}

// =============================================================
// Init
// =============================================================

function init() {
  setupPinGate({ onSuccess: () => els.app.classList.remove('hidden') });

  // File upload + drop zone
  els.emptyState.addEventListener('click', () => els.fileInput.click());
  els.emptyState.addEventListener('dragover', (e) => {
    e.preventDefault();
    els.emptyState.classList.add('dragover');
  });
  els.emptyState.addEventListener('dragleave', () => els.emptyState.classList.remove('dragover'));
  els.emptyState.addEventListener('drop', (e) => {
    e.preventDefault();
    els.emptyState.classList.remove('dragover');
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  });
  els.fileInput.addEventListener('change', (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
  });

  // Sidebar reset
  els.resetModule.addEventListener('click', resetToEmpty);

  // Capture
  els.captureButton.addEventListener('click', onCaptureClick);
  els.newCapture.addEventListener('click', onNewCapture);

  // Chat
  els.instructionInput.addEventListener('input', refreshButtons);
  els.instructionInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSend();
    }
  });
  els.sendButton.addEventListener('click', onSend);
  els.lockButton.addEventListener('click', onLock);

  // Tabs
  document.querySelectorAll('.tab').forEach((tab) => {
    tab.addEventListener('click', () => switchTab(tab.dataset.tab));
  });

  updateSpendPill();
  refreshButtons();
}

init();
